import { useFormikContext } from 'formik';
import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { useState } from 'react';
import { EditPartModal } from './EditPartModal';
import { motion, AnimatePresence } from 'framer-motion';

export interface PartsItem {
  request_type: string;
  part_number: string;
  part_description: string;
}

export const PartsTable = () => {
  const { values, setFieldValue } = useFormikContext<{ parts: PartsItem[] }>();
  const [editingPartIndex, setEditingPartIndex] = useState<number | null>(null);

  const parts = values.parts ?? [];

  const handleDelete = (index: number) => {
    const newParts = [...parts];
    newParts.splice(index, 1);
    setFieldValue('parts', newParts);
  };

  const handleEdit = (index: number) => {
    setEditingPartIndex(index);
  };

  const handleCloseModal = () => {
    setEditingPartIndex(null);
  };

  return (
    <>
      {!!parts.length ? (
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Request Type
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Part Number
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Part Description
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <AnimatePresence>
                    {parts?.map((part, i) => (
                      <motion.tr
                        key={`part-${i}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {part.request_type}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {part.part_number}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {part.part_description}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                          <button
                            type="button"
                            onClick={() => handleEdit(i)}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(i)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      <EditPartModal
        isOpen={editingPartIndex !== null}
        onClose={handleCloseModal}
        partIndex={editingPartIndex}
      />
    </>
  );
};
